import React, { useState } from 'react';
import theme from '../../theme/globalTheme';
import {
  Card,
  Button,
  Upload,
  message,
  Table,
  Divider,
  Alert,
  Space,
  Tag,
  Typography
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { PDFDocument } from 'pdf-lib';

const { Title, Text } = Typography;

function BerthPDFFieldValidator() {
  const [loading, setLoading] = useState(false);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [fields, setFields] = useState([]);

  const handleFileUpload = async (file) => {
    setLoading(true);
    setPdfInfo(null);
    setFields([]);

    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Load PDF
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      console.log('=== PDF Validation Started (Places à Quai) ===');

      // Get basic PDF info
      const pageCount = pdfDoc.getPageCount();
      const title = pdfDoc.getTitle() || 'Sans titre';

      setPdfInfo({
        fileName: file.name,
        pageCount,
        title,
        size: (file.size / 1024).toFixed(2) + ' KB'
      });

      // Try to get form fields
      let formFields = [];
      let hasForm = false;

      try {
        const form = pdfDoc.getForm();
        const formFieldsList = form.getFields();
        hasForm = true;

        console.log(`Found ${formFieldsList.length} form fields`);

        formFields = formFieldsList.map((field, index) => {
          const fieldName = field.getName();
          const fieldType = field.constructor.name;

          let fieldValue = '';
          let isRequired = false;
          let isReadOnly = false;

          try {
            // Try to get field properties
            if (fieldType === 'PDFTextField') {
              fieldValue = field.getText() || '';
              isReadOnly = field.isReadOnly();
            } else if (fieldType === 'PDFCheckBox') {
              fieldValue = field.isChecked() ? 'Checked' : 'Unchecked';
            } else if (fieldType === 'PDFDropdown') {
              const options = field.getOptions();
              fieldValue = `Options: ${options.join(', ')}`;
            }
          } catch (e) {
            console.warn(`Could not get properties for field: ${fieldName}`);
          }

          return {
            key: index,
            name: fieldName,
            type: fieldType,
            value: fieldValue,
            isReadOnly,
            isRequired
          };
        });

        setFields(formFields);

        console.log('Field details:', formFields);

      } catch (e) {
        console.warn('No form fields found:', e.message);
        hasForm = false;
      }

      if (!hasForm) {
        message.warning('Ce PDF ne contient pas de champs de formulaire');
      } else {
        message.success(`${formFields.length} champ(s) trouvé(s) dans le PDF`);
      }

      console.log('=== PDF Validation Complete ===');

    } catch (error) {
      console.error('Error validating PDF:', error);
      message.error('Erreur lors de la validation du PDF: ' + error.message);
    } finally {
      setLoading(false);
    }

    return false; // Prevent auto upload
  };

  const columns = [
    {
      title: 'Nom du Champ',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name) => <Text strong copyable>{name}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (type) => {
        let color = 'blue';
        if (type === 'PDFTextField') color = 'green';
        if (type === 'PDFCheckBox') color = 'orange';
        if (type === 'PDFDropdown') color = 'purple';
        return <Tag color={color}>{type.replace('PDF', '')}</Tag>;
      }
    },
    {
      title: 'Valeur Actuelle',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (value) => <Text type="secondary">{value || '(vide)'}</Text>
    },
    {
      title: 'Propriétés',
      key: 'properties',
      width: '20%',
      render: (_, record) => (
        <Space>
          {record.isReadOnly && <Tag color="red">Lecture seule</Tag>}
          {record.isRequired && <Tag color="orange">Requis</Tag>}
        </Space>
      )
    }
  ];

  // Check if expected fields exist for Places à Quai
  const checkExpectedFields = () => {
    const expectedFields = [
      { key: '{{arrival_date}}', label: 'Date d\'arrivée', format: 'DD/MM/YYYY' },
      { key: '{{departure_date', label: 'Date de départ', format: 'DD/MM/YYYY' },
      { key: '{{arrival_hour}}', label: 'Heure d\'arrivée', format: 'HH:mm' },
      { key: '{{departure_hour}}', label: 'Heure de départ', format: 'HH:mm' }
    ];

    return expectedFields.map(expected => {
      // Match by field VALUE (not field name)
      const found = fields.find(f => {
        const fieldValue = f.value.trim();
        return fieldValue === expected.key;
      });

      return {
        name: expected.key,
        label: expected.label,
        format: expected.format,
        found: !!found,
        matchedField: found?.name
      };
    });
  };

  const expectedFieldsCheck = fields.length > 0 ? checkExpectedFields() : [];
  const allFieldsFound = expectedFieldsCheck.length > 0 && expectedFieldsCheck.every(c => c.found);

  return (
    <div style={{
      padding: theme.spacing.md,
      maxWidth: '100%',
      minHeight: '100vh',
      background: theme.colors.bgSecondary,
    }}>
      {/* Compact Sticky Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.md,
        background: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.effects.shadowMd,
        border: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: theme.borderRadius.md,
            background: theme.colors.primaryLight,
            border: `1px solid ${theme.colors.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: theme.effects.shadowSm,
          }}>
            ⚓
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
              Validateur PDF Places à Quai
            </Title>
            <Text style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
              Vérification des champs de formulaire
            </Text>
          </div>
        </div>
      </div>

      <Card
        style={{
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.effects.shadowMd,
          marginBottom: theme.spacing.md,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <Text style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, display: 'block', marginBottom: theme.spacing.md }}>
          Uploadez un fichier PDF pour voir tous les champs de formulaire disponibles et vérifier
          qu'ils correspondent aux variables attendues pour les documents de places à quai.
        </Text>

        <Divider style={{ margin: `${theme.spacing.md} 0` }} />

        <Upload
          beforeUpload={handleFileUpload}
          accept=".pdf"
          showUploadList={false}
        >
          <Button
            icon={<UploadOutlined />}
            loading={loading}
            size="middle"
            type="primary"
            style={{
              height: '48px',
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              borderRadius: theme.borderRadius.md,
              boxShadow: theme.effects.shadowMd,
            }}
          >
            Sélectionner un PDF
          </Button>
        </Upload>

        {pdfInfo && (
          <>
            <Divider style={{ margin: `${theme.spacing.md} 0` }}>Informations du PDF</Divider>

            <div style={{
              background: theme.colors.bgSecondary,
              padding: theme.spacing.md,
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.border}`,
              marginBottom: theme.spacing.md,
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div>
                  <Text strong style={{ color: theme.colors.textPrimary }}>Nom du fichier: </Text>
                  <Text style={{ color: theme.colors.textSecondary }}>{pdfInfo.fileName}</Text>
                </div>
                <div>
                  <Text strong style={{ color: theme.colors.textPrimary }}>Nombre de pages: </Text>
                  <Text style={{ color: theme.colors.textSecondary }}>{pdfInfo.pageCount}</Text>
                </div>
                <div>
                  <Text strong style={{ color: theme.colors.textPrimary }}>Taille: </Text>
                  <Text style={{ color: theme.colors.textSecondary }}>{pdfInfo.size}</Text>
                </div>
              </Space>
            </div>

            {fields.length > 0 && (
              <>
                <Divider style={{ margin: `${theme.spacing.md} 0` }}>Vérification des Champs Requis</Divider>

                <Alert
                  message="Champs attendus pour Places à Quai"
                  description={
                    <Space direction="vertical" style={{ width: '100%', marginTop: theme.spacing.sm }}>
                      {expectedFieldsCheck.map((check) => (
                        <div key={check.name} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.sm,
                          padding: theme.spacing.xs,
                          background: check.found ? theme.colors.successLight : theme.colors.errorLight,
                          borderRadius: theme.borderRadius.sm,
                          border: `1px solid ${check.found ? theme.colors.success : theme.colors.error}`,
                        }}>
                          {check.found ? (
                            <CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '18px' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: theme.colors.error, fontSize: '18px' }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <Text strong style={{ color: theme.colors.textPrimary, display: 'block' }}>
                              {`{{${check.name}}}`} - {check.label}
                            </Text>
                            <Text style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                              Format: {check.format}
                            </Text>
                          </div>
                          {check.found && (
                            <Tag color="green">✓ trouvé: "{check.matchedField}"</Tag>
                          )}
                          {!check.found && (
                            <Tag color="red">✗ non trouvé</Tag>
                          )}
                        </div>
                      ))}
                    </Space>
                  }
                  type={allFieldsFound ? 'success' : 'error'}
                  showIcon
                  icon={allFieldsFound ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ marginBottom: theme.spacing.md }}
                />

                <Divider style={{ margin: `${theme.spacing.md} 0` }}>Tous les Champs ({fields.length})</Divider>

                <Table
                  columns={columns}
                  dataSource={fields}
                  pagination={false}
                  bordered
                  size="small"
                  style={{ marginBottom: theme.spacing.md }}
                />

                <Alert
                  message="💡 Comment créer un PDF avec des champs de formulaire ?"
                  description={
                    <div style={{ marginTop: theme.spacing.xs }}>
                      <p style={{ color: theme.colors.textPrimary, marginBottom: theme.spacing.sm }}>
                        Pour que vos variables soient remplacées automatiquement dans le générateur de Places à Quai,
                        votre PDF doit contenir des champs de formulaire avec les noms suivants :
                      </p>
                      <div style={{
                        background: theme.colors.bgSecondary,
                        padding: theme.spacing.sm,
                        borderRadius: theme.borderRadius.sm,
                        marginBottom: theme.spacing.sm,
                      }}>
                        <ul style={{ margin: 0, paddingLeft: theme.spacing.lg, color: theme.colors.textSecondary }}>
                          <li><code style={{ background: theme.colors.bgPrimary, padding: '2px 6px', borderRadius: theme.borderRadius.sm }}>{'{{arrival_date}}'}</code> - Date d'arrivée (DD/MM/YYYY)</li>
                          <li><code style={{ background: theme.colors.bgPrimary, padding: '2px 6px', borderRadius: theme.borderRadius.sm }}>{'{{departure_date'}</code> - Date de départ (DD/MM/YYYY) ⚠️ <strong>Sans }}</strong></li>
                          <li><code style={{ background: theme.colors.bgPrimary, padding: '2px 6px', borderRadius: theme.borderRadius.sm }}>{'{{arrival_hour}}'}</code> - Heure d'arrivée (HH:mm)</li>
                          <li><code style={{ background: theme.colors.bgPrimary, padding: '2px 6px', borderRadius: theme.borderRadius.sm }}>{'{{departure_hour}}'}</code> - Heure de départ (HH:mm)</li>
                        </ul>
                      </div>
                      <p style={{ color: theme.colors.textPrimary, marginBottom: theme.spacing.xs }}>
                        <strong>Comment créer ces champs :</strong>
                      </p>
                      <ul style={{ margin: 0, paddingLeft: theme.spacing.lg, color: theme.colors.textSecondary }}>
                        <li>Utilisez <strong>Adobe Acrobat Pro</strong> : Outils → Préparer le formulaire → Ajouter un champ texte</li>
                        <li>Ou utilisez un service en ligne comme <strong>PDFescape</strong> ou <strong>Sejda</strong></li>
                        <li>Nommez chaque champ <strong>exactement</strong> comme indiqué ci-dessus</li>
                        <li>Uploadez le PDF dans Supabase Storage (bucket: <code>templates</code>)</li>
                        <li>Format de nom: <code>{'{nom-bateau}-place-a-quai.pdf'}</code> (ex: <code>elettra-place-a-quai.pdf</code>)</li>
                      </ul>
                    </div>
                  }
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              </>
            )}

            {fields.length === 0 && (
              <Alert
                message="Aucun champ de formulaire trouvé"
                description="Ce PDF ne contient pas de champs de formulaire. Vous devez créer un PDF avec des champs de formulaire pour que les variables puissent être remplacées automatiquement dans le générateur de Places à Quai."
                type="error"
                showIcon
                icon={<CloseCircleOutlined />}
                style={{ marginTop: theme.spacing.md }}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default BerthPDFFieldValidator;
